import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useManifest } from '../hooks/useManifest';
import { usePlayback } from '../hooks/usePlayback';
import { useWordTracking } from '../hooks/useWordTracking';
import { useAuth } from './AuthContext';
import { apiUrl } from '../config/api';

const NowPlayingContext = createContext(null);

/* Speed steps must mirror the Select options in MediaControlsBar.jsx so
 * that the keyboard arrow shortcuts walk the same ladder the UI exposes. */
const SPEED_STEPS = ['0.5', '0.75', '1', '1.25', '1.5', '2'];

export function NowPlayingProvider({ children }) {
  const audioRef = useRef(null);
  const [bookId, setBookId] = useState(null);
  const { token } = useAuth();

  const manifestData = useManifest(bookId);
  const playback = usePlayback(audioRef, manifestData);
  const tracking = useWordTracking(audioRef, manifestData, playback);

  // When the user opens a book for reading, stamp it into their library with
  // source='played' so it shows up in My Library alongside saved/added books.
  // Idempotent on the backend (ON CONFLICT DO UPDATE) so re-opening the same
  // book doesn't create duplicates — just refreshes the updated_at timestamp.
  const openBook = useCallback((id) => {
    setBookId(id);
    if (id && token) {
      fetch(apiUrl('/api/library/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ book_id: id, source: 'played' }),
      }).catch(() => {
        // Non-fatal — if the library write fails the book still plays.
      });
    }
  }, [token]);

  // clearBook resets both provider-level state (bookId) and playback state
  const clearBook = useCallback(() => {
    playback.resetPlayback();
    setBookId(null);
  }, [playback]);

  /* Global keyboard shortcuts (WCAG 2.1.1). Only active when a book is loaded.
   * Skipped while focus is in a form element (typing) or on a button (let the
   * browser activate it). Reads playback state via a ref so the effect doesn't
   * re-attach on every audio time update. */
  const playbackRef = useRef(playback);
  useEffect(() => {
    playbackRef.current = playback;
  });

  useEffect(() => {
    if (!bookId) return;

    const handleKeyDown = (e) => {
      const target = e.target;
      const tag = target?.tagName;
      const inForm = ['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || target?.isContentEditable;
      if (inForm) return;

      /* Don't hijack Space/Enter when a button has focus — let the browser
       * activate it. The user can get global shortcuts by clicking a
       * non-interactive surface (page body, reader text) first. */
      if ((e.key === ' ' || e.key === 'Enter') && tag === 'BUTTON') return;

      const pb = playbackRef.current;
      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          pb.togglePlayPause();
          break;
        case 'j':
        case 'J':
        case 'ArrowLeft':
          e.preventDefault();
          pb.skipBackward();
          break;
        case 'l':
        case 'L':
        case 'ArrowRight':
          e.preventDefault();
          pb.skipForward();
          break;
        case 'ArrowUp': {
          e.preventDefault();
          const idx = SPEED_STEPS.indexOf(pb.playbackSpeed);
          if (idx >= 0 && idx < SPEED_STEPS.length - 1) {
            pb.adjustSpeed(SPEED_STEPS[idx + 1]);
          }
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const idx = SPEED_STEPS.indexOf(pb.playbackSpeed);
          if (idx > 0) {
            pb.adjustSpeed(SPEED_STEPS[idx - 1]);
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [bookId]);

  const value = {
    // Provider-level
    bookId,
    setBookId: openBook,
    audioRef,

    // From useManifest
    manifest: manifestData.manifest,
    loading: manifestData.loading,
    error: manifestData.error,
    visibleChapters: manifestData.visibleChapters,
    allWords: manifestData.allWords,
    hiddenRegions: manifestData.hiddenRegions,

    // From usePlayback
    isPlaying: playback.isPlaying,
    isBuffering: playback.isBuffering,
    currentTime: playback.currentTime,
    currentChunkIndex: playback.currentChunkIndex,
    playbackSpeed: playback.playbackSpeed,
    currentChapter: playback.currentChapter,
    currentChapterIndex: playback.currentChapterIndex,
    currentChapterWords: playback.currentChapterWords,
    chapterStartTime: playback.chapterStartTime,
    chapterEndTime: playback.chapterEndTime,
    chapterDuration: playback.chapterDuration,
    chapterRelativeTime: playback.chapterRelativeTime,
    seekToGlobalTime: playback.seekToGlobalTime,
    seekToChapter: playback.seekToChapter,
    togglePlayPause: playback.togglePlayPause,
    skipForward: playback.skipForward,
    skipBackward: playback.skipBackward,
    adjustSpeed: playback.adjustSpeed,
    handleAudioPlay: playback.handleAudioPlay,
    handleAudioPause: playback.handleAudioPause,
    handleAudioEnded: playback.handleAudioEnded,

    // From useWordTracking
    currentWordIndex: tracking.currentWordIndex,

    // Composed
    clearBook,
  };

  return <NowPlayingContext.Provider value={value}>{children}</NowPlayingContext.Provider>;
}

export function useNowPlaying() {
  const ctx = useContext(NowPlayingContext);
  if (!ctx) throw new Error('useNowPlaying must be used within NowPlayingProvider');
  return ctx;
}
